import { db, storage } from "../firebase/firebaseConfig";
import {
  collection,
  getDocs,
  DocumentData,
  QueryDocumentSnapshot,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from "firebase/firestore";
import {
  Artist,
  Collections,
  Device,
  DeviceLog,
  PushNotificationLog,
  Short,
  Song,
  SongRequest,
  Token,
  Update,
  User,
} from "../types/type";
import { getDownloadURL, listAll, ref } from "firebase/storage";

export const mapDocToData = <T>(
  doc: QueryDocumentSnapshot<DocumentData>
): T => {
  return { id: doc.id, ...doc.data() } as T;
};

export const fetchAndSetData = async <T>(
  fetchFunction: () => Promise<T>,
  setState: (value: T) => void
): Promise<T> => {
  try {
    const data = await fetchFunction();
    setState(data);
    return data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw new Error("fetchAndSetData error");
  }
};

export const getLastMadeAt = async () => {
  try {
    const songCollectionRef = collection(db, Collections.SONG);
    const songQuery = query(
      songCollectionRef,
      orderBy("madeAt", "desc"),
      limit(1)
    );
    const snapshot = await getDocs(songQuery);
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return doc.data().madeAt.toDate().toISOString();
    }
    return null;
  } catch (error) {
    console.error("Error fetching last madeAt:", error);
    return null;
  }
};

export const fetchUserList = async (): Promise<User[]> => {
  try {
    const snapshot = await getDocs(collection(db, Collections.USER));
    return snapshot.docs.map((doc) => mapDocToData<User>(doc));
  } catch (error) {
    console.error("Error fetching users: ", error);
    return [];
  }
};

export const fetchSongList = async (): Promise<Song[]> => {
  try {
    const snapshot = await getDocs(collection(db, Collections.SONG));
    const totalSongList = snapshot.docs.map((doc) => mapDocToData<Song>(doc));
    console.log("totalSongList : ", totalSongList);
    return totalSongList;
  } catch (error) {
    console.error("Error fetching songs: ", error);
    return [];
  }
};

export const fetchShortList = async (): Promise<Short[]> => {
  try {
    const snapshot = await getDocs(collection(db, Collections.SHORT));
    return snapshot.docs.map((doc) => mapDocToData<Short>(doc));
  } catch (error) {
    console.error("Error fetching songs: ", error);
    return [];
  }
};

export const fetchArtistList = async (): Promise<Artist[]> => {
  try {
    const snapshot = await getDocs(collection(db, Collections.ARTIST));
    return snapshot.docs.map((doc) => mapDocToData<Artist>(doc));
  } catch (error) {
    console.error("Error fetching artists: ", error);
    return [];
  }
};

export const fetchSongRequestList = async (): Promise<SongRequest[]> => {
  try {
    const snapshot = await getDocs(collection(db, Collections.SONG_REQUEST));
    return snapshot.docs.map((doc) => mapDocToData<SongRequest>(doc));
  } catch (error) {
    console.error("Error fetching songRequests: ", error);
    return [];
  }
};

export const fetchTokenList = async (): Promise<Token[]> => {
  try {
    const snapshot = await getDocs(collection(db, Collections.TOKEN));
    return snapshot.docs.map((doc) => mapDocToData<Token>(doc));
  } catch (error) {
    console.error("Error fetching TokenList: ", error);
    return [];
  }
};

export const fetchDeviceList = async (): Promise<Device[]> => {
  try {
    const snapshot = await getDocs(collection(db, Collections.DEVICE));
    return snapshot.docs.map((doc) => mapDocToData<Device>(doc));
  } catch (error) {
    console.error("Error fetching DeviceList: ", error);
    return [];
  }
};

export const fetchDeviceLogList = async (): Promise<DeviceLog[]> => {
  try {
    const snapshot = await getDocs(collection(db, Collections.DEVICE_LOG));
    return snapshot.docs.map((doc) => mapDocToData<DeviceLog>(doc));
  } catch (error) {
    console.error("Error fetching DeviceList: ", error);
    return [];
  }
};

export const fetchHomebrewRecommendedSongList = async (): Promise<Song[]> => {
  try {
    const songCollectionRef = collection(db, Collections.SONG);
    const songQuery = query(
      songCollectionRef,
      where("tagList", "array-contains", "homebrewRecommend")
    );
    const snapshot = await getDocs(songQuery);
    return snapshot.docs.map((doc) => mapDocToData<Song>(doc));
  } catch (error) {
    console.error("Error fetching homebrew recommend songs: ", error);
    return [];
  }
};

export const fetchUpdateList = async (): Promise<Update[]> => {
  try {
    const snapshot = await getDocs(collection(db, Collections.UPDATE));
    return snapshot.docs.map((doc) => mapDocToData<Update>(doc));
  } catch (error) {
    console.error("Error fetching songs: ", error);
    return [];
  }
};

export const fetchPushNotificationLogList = async (): Promise<
  PushNotificationLog[]
> => {
  try {
    const logCollection = collection(db, Collections.PUSH_NOTIFICATION_LOG);
    const logSnapshot = await getDocs(logCollection);
    return logSnapshot.docs.map((doc) =>
      mapDocToData<PushNotificationLog>(doc)
    );
  } catch (error) {
    console.error("[error] fetchPushNotificationLog : ", error);
    return [];
  }
};

export const fetchPartialData = async <T>(
  collectionName: string,
  docId: string,
  itemIdField: string
): Promise<T[]> => {
  try {
    const snapshot = await getDocs(
      query(
        collection(db, collectionName),
        where(itemIdField, ">", docId),
        orderBy(itemIdField)
      )
    );
    console.log("fetchPartialData snapshot :", snapshot);
    return snapshot.docs.map((doc) => mapDocToData(doc));
  } catch (error) {
    console.error("Error fetchPartialData : ", error);
    return [];
  }
};

export const generateSongId = async (): Promise<string> => {
  const songCollectionRef = collection(db, Collections.SONG);
  const songQuery = query(
    songCollectionRef,
    orderBy("songId", "desc"),
    limit(1)
  ); // Order by ID in descending order
  const songSnapshot = await getDocs(songQuery);

  let newId = "00000"; // Default ID if no documents are found

  if (!songSnapshot.empty) {
    const lastDoc = songSnapshot.docs[0];
    const lastId = lastDoc.data().songId as string;

    // Increment the ID
    const lastIdNumber = parseInt(lastId, 10);
    const newIdNumber = lastIdNumber + 1;

    // Pad the new ID to ensure it has a length of 5 digits
    newId = newIdNumber.toString().padStart(5, "0");
  }

  return newId;
};

export const generateShortId = async (): Promise<string> => {
  const shortCollectionRef = collection(db, Collections.SHORT);
  const shortQuery = query(
    shortCollectionRef,
    orderBy("shortId", "desc"),
    limit(1)
  );
  const shortSnapshot = await getDocs(shortQuery);

  if (!shortSnapshot.empty) {
    const lastDoc = shortSnapshot.docs[0];
    const lastId = lastDoc.data().shortId as string;
    const lastIdNumber = parseInt(lastId, 10);
    const newIdNumber = lastIdNumber + 1;

    return newIdNumber.toString().padStart(5, "0");
  }

  return "00000";
};

export const generateHomebrewPlaylistId = async (): Promise<string> => {
  const playlistCollectionRef = collection(db, Collections.HOMEBREW_PLAYLIST);
  const playlistQuery = query(
    playlistCollectionRef,
    orderBy("playlistId", "desc"),
    limit(1)
  );
  const playlistSnapshot = await getDocs(playlistQuery);

  if (!playlistSnapshot.empty) {
    const lastDoc = playlistSnapshot.docs[0];
    const lastId = lastDoc.data().playlistId as string;
    const lastIdNumber = parseInt(lastId, 10);
    const newIdNumber = lastIdNumber + 1;

    return newIdNumber.toString().padStart(5, "0");
  }

  return "00000";
};

export const generateArtistId = async (): Promise<string> => {
  const artistCollectionRef = collection(db, Collections.ARTIST);
  const artistQuery = query(
    artistCollectionRef,
    orderBy("artistId", "desc"),
    limit(1)
  ); // Order by ID in descending order
  const artistSnapshot = await getDocs(artistQuery);

  let newId = "001"; // Default ID if no documents are found

  if (!artistSnapshot.empty) {
    const lastDoc = artistSnapshot.docs[0];
    const lastId = lastDoc.data().artistId as string;

    // Increment the ID
    const lastIdNumber = parseInt(lastId, 10);
    const newIdNumber = lastIdNumber + 1;

    // Pad the new ID to ensure it has a length of 3 digits
    newId = newIdNumber.toString().padStart(3, "0");
  }

  return newId;
};

export const fetchFilesFromStorage = async (
  path: string
): Promise<string[]> => {
  const listRef = ref(storage, path);
  const fileUrls: string[] = [];

  try {
    const res = await listAll(listRef);
    for (const item of res.items) {
      const url = await getDownloadURL(item);
      fileUrls.push(url);
    }
  } catch (error) {
    console.error("Error fetching files:", error);
  }

  return fileUrls;
};

export const fetchSongRequestListByDateRange = async (
  startDate: Date,
  endDate: Date
): Promise<SongRequest[]> => {
  try {
    const songRequestRef = collection(db, Collections.SONG_REQUEST);
    const startTimeStamp = Timestamp.fromDate(startDate);
    const endTimeStamp = Timestamp.fromDate(endDate);

    const q = query(
      songRequestRef,
      where("requestAt", ">=", startTimeStamp),
      where("requestAt", "<=", endTimeStamp),
      orderBy("requestAt", "desc")
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => mapDocToData<SongRequest>(doc));
  } catch (error) {
    console.error("Error fetching song requests: ", error);
    return [];
  }
};

export const fetchInitialSongRequests = async (): Promise<SongRequest[]> => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 1);

  return fetchSongRequestListByDateRange(startDate, endDate);
};
